import { Button } from 'components/Button/Button';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import {
  getVerificationStatus,
  handleVerificationError,
} from 'utils/apiResponseErrorHandler';
import { VERIFICATION_STATUS, VERIFICATION_TEXT } from './utils/constant';
import { ConfettiBombardment } from './ConfettiBombardment';
import './Verification.scss';

// MIN_TOKEN_LENGTH must match backend TokenBytes * 2 (hex encoding)
// See: internal/gigacore/repository/email_verification.go:20
const MIN_TOKEN_LENGTH = 48;

/**
 * Reusable Verification Page Component
 * Optimized with React.memo and useCallback for performance
 * Can be used for email verification, password reset, and other verification flows
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.type - Type of verification ('email', 'password-reset', 'generic')
 * @param {string} props.title - Page title
 * @param {string} props.subtitle - Page subtitle
 * @param {string} props.successTitle - Title shown on success
 * @param {string} props.successMessage - Message shown on success
 * @param {string} props.expiredTitle - Title shown when link expires
 * @param {string} props.expiredMessage - Message shown when link expires
 * @param {string} props.errorTitle - Title shown on error
 * @param {string} props.errorMessage - Message shown on error
 * @param {Function} props.onVerify - Function to call for verification (REQUIRED)
 * @param {Object} props.successActions - Actions to show on success
 * @param {Object} props.errorActions - Actions to show on error
 * @param {boolean} props.isRetryVerification - Whether this is a retry verification
 *
 * @returns {JSX.Element} Verification component
 *
 * @example
 * // Basic usage
 * <Verification
 *   type="email"
 *   title="Email Verification"
 *   onVerify={handleEmailVerification}
 * />
 *
 * @example
 * // With custom actions
 * <Verification
 *   type="password-reset"
 *   title="Reset Password"
 *   successActions={[{ label: "Continue", onClick: handleContinue }]}
 *   onVerify={handlePasswordReset}
 * />
 */
const Verification = ({
  type = 'generic',
  title = 'Verification',
  subtitle = 'Please verify your request to continue',
  successTitle = 'Verification Successful!',
  successMessage = 'Your verification has been completed successfully.',
  expiredTitle = 'Verification Link Expired',
  expiredMessage = 'Your verification link has expired. Please request a new one.',
  errorTitle = 'Verification Failed',
  errorMessage = 'Verification failed. Please try again.',
  onVerify,
  successActions,
  errorActions,
  isRetryVerification = false,
}) => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(VERIFICATION_STATUS.LOADING);
  const [message, setMessage] = useState('');
  const [triggerConfetti, setTriggerConfetti] = useState(false);

  const token = searchParams.get('token');
  const hasPerformedVerification = useRef(false);

  // Memoized verification result handler
  const handleVerificationResult = useCallback(
    (result) => {
      try {
        const nextStatus = getVerificationStatus(result);
        setStatus(nextStatus);

        if (nextStatus === VERIFICATION_STATUS.SUCCESS) {
          setMessage(successMessage);
          setTriggerConfetti(true); // 🎉 Trigger celebration!
        } else if (nextStatus === VERIFICATION_STATUS.EXPIRED) {
          setMessage(expiredMessage);
        } else {
          setMessage(result.error || errorMessage);
        }
      } catch (error) {
        const { errorMessage: errorMsg, status: errorStatus } =
          handleVerificationError(error);
        setStatus(errorStatus);
        setMessage(errorMsg);
      }
    },
    [errorMessage, expiredMessage, successMessage]
  );

  // Memoized verification function
  const performVerification = useCallback(
    async (isRetry = false) => {
      // Prevent multiple calls unless it's a retry
      if (!isRetry && hasPerformedVerification.current) {
        return;
      }

      try {
        // Token validation
        if (
          typeof token !== 'string' ||
          token.trim().length < MIN_TOKEN_LENGTH
        ) {
          setStatus(VERIFICATION_STATUS.ERROR);
          setMessage(VERIFICATION_TEXT.invalidToken);
          return;
        }

        if (!/^[0-9a-fA-F]{48}$/.test(token)) {
          setStatus(VERIFICATION_STATUS.ERROR);
          setMessage(VERIFICATION_TEXT.invalidToken);
          return;
        }

        // Function validation
        if (!onVerify) {
          setStatus(VERIFICATION_STATUS.ERROR);
          setMessage(VERIFICATION_TEXT.verificationFnMissing);
          return;
        }

        // Mark as performed only for initial verification
        if (!isRetry) {
          hasPerformedVerification.current = true;
        }

        setStatus(VERIFICATION_STATUS.LOADING);
        const result = await onVerify(token);
        handleVerificationResult(result);
      } catch (error) {
        const { errorMessage: errorMsg, status: errorStatus } =
          handleVerificationError(error);
        setStatus(errorStatus);
        setMessage(errorMsg);
      }
    },
    [handleVerificationResult, onVerify, token]
  );

  // Memoized action renderer
  const renderAction = useCallback(() => {
    return (
      <div className='verification-action-buttons'>
        {isRetryVerification && (
          <Button onClick={() => performVerification(true)} variant='outline'>
            Retry Verification
          </Button>
        )}
        {errorActions &&
          errorActions.length > 0 &&
          errorActions.map((action, index) => (
            <Button
              key={index}
              onClick={action.onClick}
              className={action.className}
              disabled={action.disabled}
              variant={action.variant || 'primary-gradient'}
            >
              {action.label}
            </Button>
          ))}
      </div>
    );
  }, [isRetryVerification, errorActions, performVerification]);

  // Memoized content renderer
  const renderContent = useCallback(() => {
    switch (status) {
      case VERIFICATION_STATUS.LOADING:
        return (
          <div className='verification-loading'>
            <div className='loading-spinner'></div>
            <h5>{VERIFICATION_TEXT.loadingTitle}</h5>
            <p>{VERIFICATION_TEXT.loadingMessage}</p>
          </div>
        );

      case VERIFICATION_STATUS.SUCCESS:
        return (
          <div className='verification-success'>
            <div className='success-icon'>✓</div>
            <h5>{successTitle}</h5>
            <p>{message}</p>

            {successActions && (
              <div className='action-buttons'>
                {successActions.map((action, index) => (
                  <Button
                    key={index}
                    onClick={action.onClick}
                    className={action.className}
                    disabled={action.disabled}
                    variant={action.variant || ''}
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        );

      case VERIFICATION_STATUS.EXPIRED:
        return (
          <div className='verification-expired'>
            <div className='expired-icon'>⏰</div>
            <h5>{expiredTitle}</h5>
            <p>{message}</p>
            {renderAction()}
          </div>
        );

      case VERIFICATION_STATUS.ERROR:
      default:
        return (
          <div className='verification-error'>
            <div className='error-icon'>☹️</div>
            <h5>{errorTitle}</h5>
            <p>{message}</p>
            {(isRetryVerification || errorActions) && renderAction()}
          </div>
        );
    }
  }, [
    status,
    successTitle,
    message,
    successActions,
    expiredTitle,
    errorTitle,
    renderAction,
    isRetryVerification,
    errorActions,
  ]);

  // Initial verification effect
  useEffect(() => {
    if (!token) {
      setStatus(VERIFICATION_STATUS.ERROR);
      setMessage(VERIFICATION_TEXT.noTokenProvided);
      return;
    }

    // Only run on mount - performVerification is intentionally excluded
    // because we use hasPerformedVerification ref to prevent double calls
    if (!hasPerformedVerification.current) {
      performVerification();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]); // Intentionally exclude performVerification

  return (
    <div className='verification-page' data-verification-type={type}>
      <div className='verification-card'>
        <div className='verification-header'>
          <h4>{title}</h4>
          <p className='page-description'>{subtitle}</p>
        </div>
        <div className='verification-content'>{renderContent()}</div>
      </div>
      {/* 🎉 Confetti celebration for successful verification */}
      {triggerConfetti && (
        <ConfettiBombardment
          trigger={triggerConfetti}
          duration={5000}
          onCompletion={() => setTriggerConfetti(false)}
        />
      )}
    </div>
  );
};

// Set display name for better debugging
Verification.displayName = 'Verification';

Verification.propTypes = {
  type: PropTypes.oneOf(['email', 'password-reset', 'generic']),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  successTitle: PropTypes.string,
  successMessage: PropTypes.string,
  expiredTitle: PropTypes.string,
  expiredMessage: PropTypes.string,
  errorTitle: PropTypes.string,
  errorMessage: PropTypes.string,
  onVerify: PropTypes.func.isRequired,
  successActions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      className: PropTypes.string,
      disabled: PropTypes.bool,
      variant: PropTypes.string,
    })
  ),
  errorActions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      className: PropTypes.string,
      disabled: PropTypes.bool,
      variant: PropTypes.string,
    })
  ),
  isRetryVerification: PropTypes.bool,
};

export default Verification;
export { Verification };
